/* eslint-disable no-param-reassign */
/* eslint-disable indent */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import { Formik } from 'formik';
import i18n from 'i18n';
import { getModelRequest } from 'modules/environments/actions';
import {
  ModelTypeEnum,
  EnvironmentFields,
  FormModelProps,
  ModelFields,
  ModelFormFields,
  ProviderDataFormModel,
  CloudProviderEnum,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import {
  applyI18n,
  controlMaxAndMinValues,
  controlNumberLimits,
} from 'utils/helpers';
import Button from 'components/Button';
import { useRefScrollToTop } from 'hooks';
import TitleDivider from 'components/TitleDivider';
import styles from './styles';
import {
  screenFieldsGeneral,
  screenFieldsOthers,
  screenFieldsReference,
} from './formFields';
import { initial } from './initialValues';
import validationSchema from './schema';
import {
  getOnSubmitByProvider,
  getSchemaByProviderAndSchema,
} from './cloudProviderUtils';
import MonitorSliders from './MonitorSliders';
import AwsForm from './AwsForm';
import OciForm from './OciForm';

const useStyles = makeStyles(styles);

interface Props {
  cloud?: CloudProviderEnum;
  environment: EnvironmentFields | null;
  getDetailsUrl?: string;
  initialValues?: FormModelProps;
  isLoading: boolean;
  modelFields?: ModelFields;
  modelSelected: ModelFormFields | null;
  onCancel: () => void;
  onSubmit: ({
    values,
    pathUrl,
  }: {
    values: ModelFormFields | FormModelProps;
    pathUrl: string;
  }) => void;
  getModelRequest: (props) => void;
}

const ModelTitleDivider = ({ children, marginTop = 8 }) => (
  <TitleDivider overrideClasses={{ title: { marginTop } }}>
    {children}
  </TitleDivider>
);

const FormRegisterModel = ({
  cloud,
  environment,
  getDetailsUrl,
  initialValues = initial,
  isLoading,
  modelFields,
  modelSelected,
  onCancel,
  onSubmit,
  getModelRequest,
}: Props) => {
  const classes = useStyles();
  const isMicro = environment?.behavior?.type === 'micro';
  const isEdit = !!getDetailsUrl;

  const modelType = {
    custom_image: ModelTypeEnum.customImage,
    boot_volume: ModelTypeEnum.bootVolume,
    boot_volume_backup: ModelTypeEnum.bootVolumeBackup,
  };

  const getModelType = modelType[modelFields?.model_type || ''];

  const handleSubmit = (values: ModelFormFields | FormModelProps) => {
    const requestPayload: ModelFormFields | FormModelProps = { ...values };

    if (cloud === CloudProviderEnum.AWS) {
      if (isMicro) {
        requestPayload.provider_data = {} as ProviderDataFormModel;
      }

      onSubmit({
        values: requestPayload,
        pathUrl: 'aws/ami/',
      });
    } else if (cloud === CloudProviderEnum.MULTICLOUD) {
      onSubmit({
        values: requestPayload,
        pathUrl: 'multicloud/',
      });
    } else {
      getOnSubmitByProvider(
        environment,
        onSubmit
      )(isEdit ? getModelType : environment?.publication?.type)(requestPayload);
    }
  };

  React.useEffect(() => {
    const getModelDetails = async () => {
      getModelRequest(getDetailsUrl);
    };

    if (isEdit) getModelDetails();
  }, [isEdit, getDetailsUrl, getModelRequest]);

  const getSchemaByProvider = React.useCallback(
    (cloudProvider: string, type?: ModelTypeEnum) =>
      getSchemaByProviderAndSchema(cloudProvider, validationSchema)(type),
    []
  );

  const schema = React.useMemo(() => {
    if (isMicro) return validationSchema?.micro;

    const cloudProvider = cloud || CloudProviderEnum.OCI;
    return getSchemaByProvider(
      cloudProvider,
      isEdit ? getModelType : environment?.publication?.type
    );
  }, [isMicro, cloud, getSchemaByProvider, environment]);

  const [anchorRef, scrollToAnchor] = useRefScrollToTop();

  const initialFormValues = React.useMemo(() => {
    if (isEdit) return modelSelected || initialValues;
    return initialValues;
  }, [modelSelected, isEdit, initialValues]);

  return (
    <Grid item container>
      <Formik
        initialValues={initialFormValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, submitForm, errors, values }) => (
          <Grid
            container
            spacing={2}
            className={classes.formContainer}
            ref={anchorRef}
          >
            <ModelTitleDivider marginTop={0}>
              {i18n.t('GENERAL_INFORMATION_KEY')}:
            </ModelTitleDivider>
            <GenericForm
              fields={values}
              screenFields={applyI18n(screenFieldsGeneral, i18n.language)}
              setFieldValue={setFieldValue}
              errors={errors}
            />
            {cloud === CloudProviderEnum.AWS && !isMicro && (
              <AwsForm
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            )}

            {cloud === CloudProviderEnum.OCI && !isMicro && (
              <OciForm
                values={values}
                errors={errors}
                isEdit={isEdit}
                modelType={getModelType}
                setFieldValue={setFieldValue}
              />
            )}

            <MonitorSliders
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
            />

            <ModelTitleDivider>
              {i18n.t('TEMPERATURE_REFERENCE_KEY')}:
            </ModelTitleDivider>
            <Grid
              item
              container
              spacing={2}
              className={classes.smallFieldsWrap}
            >
              <GenericForm
                fields={{
                  ...values,
                  temperature_empty: controlNumberLimits(
                    values.temperature_empty.toString()
                  ),
                  temperature_operational: controlNumberLimits(
                    values.temperature_operational.toString()
                  ),
                  temperature_stopped: controlNumberLimits(
                    values.temperature_stopped.toString()
                  ),
                }}
                screenFields={applyI18n(
                  screenFieldsReference.map(item => {
                    values.temperature_empty = Number(
                      controlMaxAndMinValues(values.temperature_empty)
                    );
                    values.temperature_operational = Number(
                      controlMaxAndMinValues(values.temperature_operational)
                    );
                    values.temperature_stopped = Number(
                      controlMaxAndMinValues(values.temperature_stopped)
                    );

                    return {
                      ...item,
                    };
                  }),
                  i18n.language
                )}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            </Grid>

            <ModelTitleDivider>{i18n.t('OTHERS_KEY')}</ModelTitleDivider>
            <Grid
              item
              container
              spacing={2}
              className={classes.smallFieldsWrap}
            >
              <GenericForm
                fields={values}
                screenFields={applyI18n(screenFieldsOthers, i18n.language)}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            </Grid>

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button
                  id="submit-button"
                  submitButton
                  onClick={event => {
                    if (!Object.keys(errors)?.length) {
                      submitForm();
                    } else {
                      scrollToAnchor(event);
                    }
                  }}
                  disabled={isLoading}
                >
                  {i18n.t(isEdit ? 'SAVE_KEY' : 'CREATE_MODEL_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, isLoading, modelSelected },
}: ApplicationState) => ({
  environment: environmentSelected,
  modelSelected,
  isLoading,
});

export default connect(mapStateToProps, {
  getModelRequest,
})(FormRegisterModel);
